import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { ColumnDef } from "@tanstack/react-table";
import { RowData } from "components/Table";
import CONSTANTS from "core/constants";
import { format } from "date-fns";
import { TFunction } from "react-i18next";

const fallbackValue = "--";

export type CanceledSlotsTableRowProps = RowData & {
  patientName: string | typeof fallbackValue;
  psychologistUserId: number | null | typeof fallbackValue;
  patientUserId: number | null | typeof fallbackValue;
  slotId: number | typeof fallbackValue;
  canceledAt: string | typeof fallbackValue;
  canceledBy: string | typeof fallbackValue;
  date: string | typeof fallbackValue;
  time: string | typeof fallbackValue;
  status: string | typeof fallbackValue;
};

export const columns: ColumnDef<CanceledSlotsTableRowProps>[] = [
  {
    header: "USER_ID",
    accessorKey: "patientUserId",
    cell: (info) => info.getValue(),
    meta: {
      sensitive: true,
    },
  },
  {
    header: "PATIENT",
    accessorKey: "patientName",
    cell: (info) => info.getValue(),
    meta: {
      sensitive: true,
    },
  },
  {
    header: "BOOKING_ID",
    accessorKey: "slotId",
    cell: (info) => info.getValue(),
  },
  {
    header: "CANCELED_AT",
    accessorKey: "canceledAt",
    cell: (info) => info.getValue(),
  },
  {
    header: "CANCELED_BY",
    accessorKey: "canceledBy",
    cell: (info) => info.getValue(),
  },
  {
    header: "DATE",
    accessorKey: "date",
    cell: (info) => info.getValue(),
  },
  {
    header: "TIME",
    accessorKey: "time",
    cell: (info) => info.getValue(),
  },
  {
    header: "STATUS",
    accessorKey: "status",
    cell: (info) => info.getValue(),
  },
];

export const formatCanceledSlotsTableData = (
  data: RouterOutput["slots"]["getPsychologistSlotsInTimeFrame"] | undefined,
  t: TFunction,
): CanceledSlotsTableRowProps[] => {
  const fallBackData: CanceledSlotsTableRowProps[] = [
    {
      patientUserId: fallbackValue,
      psychologistUserId: fallbackValue,
      patientName: fallbackValue,
      slotId: fallbackValue,
      canceledAt: fallbackValue,
      canceledBy: fallbackValue,
      date: fallbackValue,
      time: fallbackValue,
      status: fallbackValue,
      id: fallbackValue,
    },
  ];

  if (!data) {
    return fallBackData;
  }

  const filteredData = data
    .filter(
      (b) =>
        b.status === CONSTANTS.BOOKING_STATUS.CANCELED ||
        b.status === CONSTANTS.BOOKING_STATUS.LATE_CANCELED,
    )
    .map((booking) => {
      const {
        patientFirstName,
        patientLastName,
        patientUserId,
        slotId,
        status,
        psychologistUserId,
      } = booking;

      const patientName = `${patientFirstName} ${patientLastName}`;

      const date = format(
        new Date(booking.startsAt),
        CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
      );
      const startTime = format(
        new Date(booking.startsAt),
        CONSTANTS.DEFAULT_TIME_FORMAT,
      );
      const endTime = format(
        new Date(booking.endsAt),
        CONSTANTS.DEFAULT_TIME_FORMAT,
      );
      const time = `${startTime} ${t("TO")} ${endTime}`;

      const canceledAt = booking.canceledAt
        ? format(
            new Date(booking.canceledAt),
            CONSTANTS.DATE_FORMAT.YEAR_NAME_DATE_FORMAT,
          )
        : fallbackValue;

      const canceledBy = (
        slot: RouterOutput["slots"]["getPsychologistSlotsInTimeFrame"][0],
      ) => {
        if (slot.canceledBy === slot.patientUserId) {
          return t("PATIENT");
        }

        if (slot.canceledBy === slot.psychologistUserId) {
          return t("PSYCHOLOGIST");
        }
        if (slot.canceledBy) {
          return t("ADMIN");
        }
        return fallbackValue;
      };

      return {
        psychologistUserId,
        patientUserId,
        patientName,
        slotId,
        canceledAt,
        canceledBy: canceledBy(booking),
        date,
        time,
        id: `${booking.slotId}`,
        status:
          status === CONSTANTS.BOOKING_STATUS.CANCELED
            ? t("CANCELED")
            : t("LATE_CANCELED"),
      };
    });

  return filteredData.length ? filteredData : fallBackData;
};
